<template>
 <div>
        <div class="level is-vcentered">
            <div class="level-left">
                <h1 class="title is-size-4">Netbase Tokens</h1>
            </div>
            <div class="level-right">
                <router-link class="level-item button is-outlined" to="/Admin">Back to Admin</router-link>
            </div>
        </div>
        <b-notification type="is-danger" v-if="errorMessage" :closable="false">
            {{ errorMessage }}
        </b-notification>
        <!-- Search input -->
        <b-field>
            <b-input
                rounded
                v-model="searchName"
                placeholder="search..."
                icon="search"
            >
            </b-input>
        </b-field>

        <!-- Token Table -->
        <b-table
            class="box"
            :data="groupedTokens"
            :pagination-simple="false"
            :default-sort-direction="'asc'"
            :loading="isLoading"
            :striped="true"
            default-sort="id"
            detailed
            detail-key="id"
        >
            <template slot-scope="props">
                <b-table-column field="id" label="ID" width="100" numeric centered sortable>
                    {{ props.row.id }}
                </b-table-column>

                <b-table-column field="token" label="Token" sortable >
                    <span>{{ props.row.accessToken }}</span>
                </b-table-column>
                <b-table-column field="totalProjectCount" label="Projects" sortable>
                    {{ props.row.totalProjectCount }}
                </b-table-column>
                <b-table-column field="expiresOn" label="Expires On (UTC)" centered sortable>
                    {{ props.row.expiresOn | formatDate }}
                </b-table-column>
                <b-table-column field="lastModified" label="Last Modified (UTC)" centered sortable>
                    {{ props.row.lastModified | formatDate }}
                </b-table-column>
                <b-table-column field="created" label="Created" centered sortable>
                    {{ props.row.created | formatDate }}
                </b-table-column>

                <b-table-column label="Delete" width="100" centered>
                    <button class="button is-outlined is-inline-block" @click="openDeleteTokenConfirmModal(props.row)">
                        <b-icon
                            icon="trash-alt"
                            type="is-danger"
                        >
                        </b-icon>
                    </button>
                </b-table-column>
                <b-table-column label="Associate" width="100" centered>
                    <button class="button is-outlined is-inline-block" @click="openUserAssociationModal(props.row)">
                        <b-icon
                            icon="link"
                            type="is-info"
                        >
                        </b-icon>
                    </button>
                </b-table-column>
            </template>

            <!-- Token User Details -->
            <template slot="detail" slot-scope="props">
                <div class="token-user-container" v-if="props.row.users.length > 0">
                    <div class="token-user" v-for="user in props.row.users" :key="user.id">
                        <div>{{ user.email }}</div>
                        <span> {{ user.projectCount }} active project{{ user.projectCount == 1 ? '' : 's' }}</span>
                        <div class="association-info">
                            <span class="is-pulled-right is-italic">Associated Date (UTC): {{ user.associatedDate | formatDate }}</span>
                            <button class="button is-outlined is-small is-pulled-right" @click="openDeleteTokenUserConfirmModal(props.row, user)">
                                <b-icon
                                    icon="unlink"
                                    type="is-info"
                                >
                                </b-icon>
                            </button>
                        </div>
                    </div>
                </div>
                <div class="empty-users has-text-grey" v-else>
                    <p>No users have been associated with this token</p>
                </div>
            </template>
            <!-- Empty Table Template -->
            <template slot="empty">
                <empty-table-content/>
            </template>
        </b-table>
    </div>
</template>

<script>
import netBaseService from '@/services/netbase-service';
import _ from 'lodash';
import EmptyTableContent from '@/components/EmptyTableContent.vue';
import TokenUserAssocationForm from '@/components/TokenUserAssociationForm.vue';

export default {
    name: 'NetbaseTokenAdmin',
    components: {
        EmptyTableContent
    },
    data () {
        return {
            errorMessage: '',
            searchName: '',
            nbTokenArray: [],
            isLoading: true
        };
    },
    created () {
        netBaseService.getNetBaseAccessTokens()
            .then(response => {
                this.nbTokenArray = response.data;
            })
            .catch(error => {
                this.errorMessage = error.message;
            })
            .finally(() => {
                this.isLoading = false;
            });
    },
    methods: {
        deleteToken (token) {
            netBaseService.deleteNetBaseAccessToken(token.id)
                .then(response => {
                    this.nbTokenArray = this.nbTokenArray.filter(t => t.netBaseAccessToken.id !== token.id);
                    this.$buefy.toast.open({
                        message: `Token ${token.accessToken} has been deleted`,
                        type: 'is-success'
                    });
                })
                .catch(error => this.$buefy.toast.open({
                    message: `Error during token deletion: ${error.message}`,
                    type: 'is-danger'
                }));
        },
        dissociateUser (token, user) {
            netBaseService.deleteNetBaseAccessTokenUser(token.id, user.id)
                .then(response => {
                    this.nbTokenArray = this.nbTokenArray.filter(
                        t => t.netBaseAccessToken.id !== token.id || t.userId !== user.id
                    );
                    this.$buefy.toast.open({
                        message: `${user.email} has successfully been dissociated from Token ${token.accessToken}`,
                        type: 'is-success'
                    });
                })
                .catch(error => this.$buefy.toast.open({
                    message: `Error while dissociating user: ${error.message}`,
                    type: 'is-danger'
                }));
        },
        openDeleteTokenConfirmModal (token) {
            this.$buefy.dialog.confirm({
                title: 'Delete Token',
                message: `<p>Are you sure you want to delete the following token? </p>
                    <br/>
                    <p class="has-text-centered"><b>${token.accessToken}</b></p>
                    <br/>
                    <p>Removing this token may impact project data retrieval for users associated with this token.</p>`,
                onConfirm: () => this.deleteToken(token)
            });
        },
        openDeleteTokenUserConfirmModal (token, user) {
            this.$buefy.dialog.confirm({
                title: 'Dissociate Token User',
                message: `
                Are you sure you want to dissociate user:
                <br/>
                <div class="has-text-centered" style="margin: 1em auto;">
                    <p><b>${user.email}</b></p>

                </div>
                <p> from the following token? </p>
                <div class="has-text-centered" style="margin: 1em auto;">
                    <p><b>${token.accessToken}</b><p>
                </div>
                <p>Removing this association may impact project data retrieval for the user.</p>`,
                onConfirm: () => this.dissociateUser(token, user)
            });
        },
        openUserAssociationModal (token) {
            this.$buefy.modal.open({
                parent: this,
                component: TokenUserAssocationForm,
                hasModalCard: true,
                props: {
                    token: token
                },
                events: {
                    submitEvent: (selectedUsers) => this.updateTokenAssociation(token, selectedUsers)
                }
            });
        },
        async updateTokenAssociation (token, selectedUsers) {
            const users = selectedUsers.map(user => ({
                created: Date.now(),
                netBaseAccessToken: token,
                projectCount: user.projectCount,
                userEmail: user.userEmail,
                userId: user.userId
            }));

            this.nbTokenArray = this.nbTokenArray.concat(users);
            
            const emails = selectedUsers.map(user => user.userEmail).join(', ');
            this.$buefy.toast.open({
                message: `Users ${emails} have successfully been associated with Token ${token.accessToken}` ,
                type: 'is-success'
            });
        },
    },
    computed: {
        groupedTokens () {
            let result = [];
            let sourceArray = [];

            if (this.searchName) {
                // Filter the list with the search terms first
                sourceArray = this.nbTokenArray.filter(tokenUser => {
                    return tokenUser.netBaseAccessToken.id
                        .toString()
                        .toLowerCase()
                        .includes(this.searchName.toLowerCase()) ||
                            tokenUser.netBaseAccessToken.accessToken
                                .toLowerCase()
                                .includes(this.searchName.toLowerCase()) ||
                            (tokenUser.userEmail && tokenUser.userEmail
                                .toLowerCase()
                                .includes(this.searchName.toLowerCase()));
                }
                );
            } else {
                sourceArray = this.nbTokenArray;
            }

            if (sourceArray.length > 0) {
                let groupedTokenUsers = _.groupBy(sourceArray, tokenUser => tokenUser.netBaseAccessToken.id);
                _.forEach(groupedTokenUsers, tokenGroup => {
                    let token = {
                        ...tokenGroup[0].netBaseAccessToken,
                        users: [],
                        totalProjectCount: 0
                    };
                    _.forEach(tokenGroup, u => {
                        if (u.userEmail) {
                            token.users.push({ id: u.userId, email: u.userEmail, associatedDate: u.created, projectCount: u.projectCount });
                            token.totalProjectCount += parseInt(u.projectCount, 10); // because json response.data is treating the int as a string
                        }
                    });
                    result.push(token);
                });
            }
            return result;
        }
    }
};
</script>

<style scoped lang="scss">
.token-user{
    margin:0.5em;
    padding: 0.25em;
    border-bottom:0.5px solid $border;
    display: flex;
    justify-content: space-between;
    align-items: baseline;

    .association-info{
        display: flex;
        align-items: baseline;
        span {
            font-size: 0.8em;
            font-weight: lighter;
        }
        button {
            margin-left: 1em;
        }
    }
}
.empty-users {
    font-style: italic;
}
</style>
